import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { AppShell } from "@/components/layout/app-shell";
import { Button } from "@/components/ui/button";
import { ResultsDashboard } from "@/components/results/results-dashboard";
import { Loader2, Edit, ArrowLeft } from "lucide-react";
import { Helmet } from 'react-helmet';
import { resolveUrl } from "@/lib/queryClient";

export default function ResultsPage() {
  const { t } = useTranslation();
  const params = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const id = params.id;

  // Fetch analysis details
  const { data: analysis, isLoading, error } = useQuery<any>({
    queryKey: [`/api/analyses/${id}`],
    enabled: !!id,
  });

  // Handle submission of pending analysis
  const submitAnalysis = async () => {
    try {
      await fetch(resolveUrl(`/api/analyses/${id}/submit`), {
        method: "POST",
        credentials: "include",
      });
      // Reload the page to see results
      window.location.reload();
    } catch (error) {
      console.error("Error submitting analysis:", error);
    }
  };

  if (isLoading) {
    return (
      <AppShell>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <p>{t("results.loading")}</p>
        </div>
      </AppShell>
    );
  }

  if (error || !analysis) {
    return (
      <AppShell>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <h2 className="text-xl font-semibold mb-2">{t("results.error")}</h2>
          <p className="text-muted-foreground mb-4">{t("results.notFound")}</p>
          <Button onClick={() => navigate("/analyses")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("results.backToAnalyses")}
          </Button>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <Helmet>
        <title>{`${analysis.manufacturer} ${analysis.model} | Pump ECM`}</title>
        <meta name="description" content={`Analysis results for ${analysis.manufacturer} ${analysis.model} pump/motor`} />
      </Helmet>

      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">
            {analysis.manufacturer} {analysis.model}
          </h1>
          <p className="text-muted-foreground">
            {t("results.created")}: {new Date(analysis.createdAt).toLocaleDateString()} | 
            {t("results.status")}: {t(`results.statusLabels.${analysis.status}`)}
          </p>
        </div>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            onClick={() => navigate("/analyses")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("results.backToList")}
          </Button>
          
          {analysis.status !== "completed" && (
            <>
              <Button
                variant="outline"
                onClick={() => navigate(`/analysis/edit/${id}`)}
              >
                <Edit className="mr-2 h-4 w-4" />
                {t("results.edit")}
              </Button>
              
              <Button
                onClick={submitAnalysis}
                disabled={analysis.status === "submitted"}
              >
                {analysis.status === "submitted" ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("results.processing")}
                  </>
                ) : (
                  t("results.submitForAnalysis")
                )}
              </Button>
            </>
          )}
        </div>
      </div>

      {analysis.status === "draft" && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                {t("results.draftNotice")}
              </p>
            </div>
          </div>
        </div>
      )}

      {analysis.status === "submitted" && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-8">
          <div className="flex">
            <div className="flex-shrink-0">
              <Loader2 className="h-5 w-5 text-blue-400 animate-spin" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                {t("results.processingNotice")}
              </p>
            </div>
          </div>
        </div>
      )}

      <ResultsDashboard analysis={analysis} />
    </AppShell>
  );
}
